/**
 * @fileoverview Custom hook for role-based redirection.
 * @summary Handles automatic redirection of authenticated users based on their role.
 *
 * @package KaraoNow
 * @module apps/front/src/hooks/useRoleBasedRedirect
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppSelector } from './redux.js'
import { ADMIN_DASHBOARD_URL } from '../config/constants.js'

/**
 * @summary Custom hook to handle role-based redirection after authentication.
 * @description Automatically redirects authenticated users to their appropriate dashboard
 * based on their role (organizer, singer, superadmin).
 * @param {boolean} enabled - Whether the redirect should be active (default: true)
 * @param {string | null} redirectParam - Optional redirect parameter to override default behavior
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export function useRoleBasedRedirect(enabled: boolean = true, redirectParam?: string | null) {
  const navigate = useNavigate()
  const { isAuthenticated, user } = useAppSelector((state) => state.auth)

  useEffect(() => {
    if (!enabled || !isAuthenticated || !user) return

    // If redirectParam is provided, use it (highest priority)
    if (redirectParam) {
      navigate(redirectParam, { replace: true })
      return
    }

    // Otherwise, redirect based on role
    // Note: Keeping current logic - using window.location.href for all redirects
    // This will be improved later to use navigate() for same-domain redirects
    if (user.role === 'superadmin') {
      window.location.href = ADMIN_DASHBOARD_URL
    } else if (user.role === 'organizer') {
      navigate('/dashboard', { replace: true })
    } else if (user.role === 'singer') {
      navigate('/singer/dashboard', { replace: true })
    }
  }, [enabled, isAuthenticated, user, navigate, redirectParam])
}
